import type { Incident, Track, WorkOrder } from '@/api/types'
import { PipelineSm } from './Pipeline'
import { CardSkeleton, RefreshButton, StateBadge } from './atoms'

interface Props {
  incident: Incident
  workOrders: WorkOrder[]
  loading: boolean
  refreshing: boolean
  error: string | null
  track: Track
  selectedId: string | null
  onSelect: (ot: WorkOrder) => void
  onRefresh: () => void
}

/** Sección 3 · Órdenes de trabajo. */
export function WorkOrderList({
  incident,
  workOrders,
  loading,
  refreshing,
  error,
  track,
  selectedId,
  onSelect,
  onRefresh,
}: Props) {
  const n = workOrders.length

  return (
    <section className="sec sec-ots" aria-labelledby="h-ots">
      <div className="sec-hd">
        <h2 id="h-ots">Órdenes de trabajo</h2>
        <RefreshButton
          onClick={onRefresh}
          busy={refreshing || loading}
          label="Actualizar las órdenes de esta incidencia"
        />
      </div>
      <div className="sec-in">
        <article className="card">
          {loading ? (
            <>
              <h3 style={{ fontSize: 15, fontWeight: 650, color: 'var(--ink)' }}>
                Buscando las órdenes de esta incidencia…
              </h3>
              <div style={{ marginTop: 14 }}>
                <CardSkeleton />
              </div>
            </>
          ) : error ? (
            <div className="empty" role="alert">
              <b>No se pudieron cargar las órdenes</b>
              {error}
            </div>
          ) : n === 0 && incident.otCount > 0 ? (
            /*
             * El caso confuso: la tarjeta dice "3 OTs" y acá no hay ninguna
             * fila. Pasa porque el contador sale del largo de los arreglos de
             * la incidencia, sin mirar el estado de cada orden (corrección
             * §2.4), y las dadas de baja no se listan. Decir "todavía no hay
             * órdenes" acá sería mentir: sí las hubo, ya no están activas.
             */
            <div className="empty">
              <b>Ninguna orden activa para esta incidencia</b>
              La tarjeta cuenta {incident.otCount} {incident.otCount === 1 ? 'orden' : 'órdenes'},
              pero {incident.otCount === 1 ? 'esa orden fue dada' : 'esas órdenes fueron dadas'} de
              baja y no {incident.otCount === 1 ? 'se lista' : 'se listan'} acá. Si el problema
              sigue en pie, avísale a Mantenimiento/TI para que genere una nueva.
            </div>
          ) : n === 0 ? (
            <div className="empty">
              <b>Todavía no hay órdenes de trabajo</b>
              Cuando Mantenimiento/TI genere una orden para esta incidencia, aparecerá acá.
            </div>
          ) : (
            <>
              <h3 style={{ fontSize: 15, fontWeight: 650, color: 'var(--ink)' }}>
                {n} OT{n === 1 ? '' : 's'} abierta{n === 1 ? '' : 's'} para esta incidencia
              </h3>
              {n > 1 ? (
                <p className="subtitle" style={{ fontSize: 13, marginBottom: 14 }}>
                  Varios proveedores trabajando en paralelo sobre el mismo problema.
                </p>
              ) : (
                <div style={{ height: 12 }} />
              )}

              {workOrders.map((ot) => (
                <button
                  key={ot._id}
                  type="button"
                  className="ot-row"
                  aria-current={selectedId === ot._id}
                  onClick={() => onSelect(ot)}
                >
                  <div className="ot-top">
                    <div style={{ minWidth: 0 }}>
                      <p className="ot-name">{ot.name}</p>
                      <p className="ot-prov">{ot.provider.display}</p>
                    </div>
                  </div>
                  <div className="ot-bot">
                    <PipelineSm state={ot.state} track={track} />
                    <StateBadge state={ot.state} />
                  </div>
                </button>
              ))}

              {/* El contador de la tarjeta sale del largo de los arreglos e
                  incluye órdenes dadas de baja (corrección §2.4), así que
                  puede superar a las filas. Se dice en vez de disimularlo. */}
              {incident.otCount > n && (
                <p className="meta">
                  ⚠️ El contador de la tarjeta dice {incident.otCount} porque incluye órdenes
                  dadas de baja, que no se listan.
                </p>
              )}
            </>
          )}
        </article>
      </div>
    </section>
  )
}
